import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { runNapAudit, type NapAuditResult } from "@/lib/napAudit";

/**
 * Floating dev badge that runs a NAP consistency audit on every route change
 * and surfaces any mismatches between the rendered DOM and the canonical
 * clinicNAP source (plus a cross-check against JSON-LD schema).
 *
 * Visible in development by default, and in production only when the URL
 * contains `?nap-check=1` (so you can spot-check published pages without
 * exposing the badge to ordinary visitors).
 */
const NapAuditBadge = () => {
  const location = useLocation();
  const [result, setResult] = useState<NapAuditResult | null>(null);
  const [open, setOpen] = useState(false);

  const enabled =
    !import.meta.env.PROD ||
    new URLSearchParams(location.search).get("nap-check") === "1";

  useEffect(() => {
    if (!enabled) return;
    // Defer one tick so React has committed the new route's DOM.
    const t = window.setTimeout(() => {
      const r = runNapAudit();
      setResult(r);
      if (!r.ok) {
        // eslint-disable-next-line no-console
        console.warn("[NAP audit] Issues on", r.url, {
          failingFields: r.items.filter((i) => !i.ok).map((i) => i.field),
          schemaIssues: r.schemaIssues,
          full: r,
        });
      }
    }, 200);
    return () => window.clearTimeout(t);
  }, [enabled, location.pathname, location.search]);

  if (!enabled || !result) return null;

  const failing = result.items.filter((i) => !i.ok);
  const conflicts = result.items.flatMap((i) => i.conflicts);
  const ok = result.ok;

  return (
    <div className="fixed bottom-4 left-4 z-[70] font-sans text-xs">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-2 px-3 py-2 rounded-full shadow-lg backdrop-blur-md border transition-colors ${
          ok
            ? "bg-emerald-50/95 border-emerald-200 text-emerald-900 hover:bg-emerald-100"
            : "bg-red-50/95 border-red-200 text-red-900 hover:bg-red-100"
        }`}
        aria-expanded={open}
        aria-label="Toggle NAP consistency audit"
      >
        <span
          aria-hidden
          className={`inline-block w-2 h-2 rounded-full ${
            ok ? "bg-emerald-500" : "bg-red-500 animate-pulse"
          }`}
        />
        <span className="font-semibold tracking-wide uppercase text-[10px]">
          NAP {ok ? "OK" : `${failing.length + conflicts.length} issue${failing.length + conflicts.length === 1 ? "" : "s"}`}
        </span>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="NAP consistency report"
          className="mt-2 w-[22rem] max-w-[calc(100vw-2rem)] bg-white border border-slate-200 rounded-xl shadow-xl p-4 text-slate-800 space-y-3"
        >
          <div className="flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500">
              NAP Audit · {result.url}
            </p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-slate-400 hover:text-slate-700 text-base leading-none"
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <ul className="space-y-2">
            {result.items.map((item) => (
              <li key={item.field} className="flex items-start gap-2">
                <span
                  aria-hidden
                  className={`mt-1 inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                    item.ok ? "bg-emerald-500" : "bg-red-500"
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-medium">{item.label}</span>
                    <span className="text-[10px] text-slate-500">
                      {item.occurrences}× found
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-600 truncate">
                    Expected: <span className="font-mono">{item.expected}</span>
                  </p>
                  {item.conflicts.length > 0 && (
                    <p className="text-[11px] text-red-700 mt-0.5">
                      Conflicting on page:{" "}
                      <span className="font-mono">{item.conflicts.join(", ")}</span>
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>

          <div className="pt-2 border-t border-slate-200">
            <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">
              JSON-LD schema
            </p>
            {result.schemaIssues.length === 0 ? (
              <p className="text-[11px] text-emerald-700">
                Schema matches the visible NAP.
              </p>
            ) : (
              <ul className="space-y-1">
                {result.schemaIssues.map((issue) => (
                  <li key={issue} className="text-[11px] text-red-700">
                    • {issue}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="pt-2 border-t border-slate-200">
            <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">
              Header / footer ↔ JSON-LD
            </p>
            {!result.schemaVsWidgets.hasWidget ? (
              <p className="text-[11px] text-slate-500">
                No header/footer NAP widget on this page.
              </p>
            ) : result.schemaVsWidgets.ok ? (
              <p className="text-[11px] text-emerald-700">
                JSON-LD aligns with the header & footer.
              </p>
            ) : (
              <ul className="space-y-1">
                {result.schemaVsWidgets.fields
                  .filter((f) => !f.ok)
                  .map((f) => (
                    <li key={f.field} className="text-[11px] text-red-700">
                      • <span className="font-medium">{f.label}:</span>{" "}
                      <span className="font-mono">{f.rendered ?? "—"}</span> vs{" "}
                      <span className="font-mono">{f.schema ?? "—"}</span>
                    </li>
                  ))}
              </ul>
            )}
          </div>

          <p className="text-[10px] text-slate-400 pt-1">
            Edit <span className="font-mono">src/lib/clinic.ts</span> to update the source of truth.
          </p>
        </div>
      )}
    </div>
  );
};

export default NapAuditBadge;
